import { EventBus } from '~/rage/common';
import { notifyOnError } from '~/rage/decorators';
import { ClientError, IAuthPayload } from '~/shared';
import { IRpcService } from '../RpcService';
import { jwtDecode } from './helpers';
import { RestTransport } from './transports/RestTransport';
import { SocketTransport } from './transports/SocketTransport';

export class BackendService {
	private isConnected: boolean = false;
	private isFirstConnection: boolean = true;
	private isTokenSet: boolean = false;

	private readonly rest: RestTransport;
	private readonly socket: SocketTransport;

	private initPromise: Promise<void> | null = null;
	private initResolve: (() => void) | null = null;

	private tokenRefreshInterval: NodeJS.Timer | null = null;
	private tokenRefreshPromise: Promise<void> | null = null;
	private tokenRefreshResolve: (() => void) | null = null;

	constructor(private readonly rpc: IRpcService) {
		this.rest = new RestTransport();
		this.socket = new SocketTransport();

		this.initPromise = new Promise((resolve) => {
			this.initResolve = resolve;
		});

		this.rpc.register('backend:setUrl', this.handleSetUrl.bind(this));
		this.rpc.register('backend:setToken', this.handleSetToken.bind(this));
		this.rpc.register('backend:connect', this.handleConnect.bind(this));
		this.rpc.register('backend:disconnect', this.handleDisconnect.bind(this));
		this.rpc.register('backend:invoke', this.handleInvoke.bind(this));

		this.waitForInit().then(async () =>
			this.rpc.invokeServer('auth:playerBackendConnected'),
		);
	}

	public isInitialized(): boolean {
		return this.initPromise === null;
	}

	public async waitForInit(): Promise<void> {
		await this.initPromise;
	}

	private handleSetUrl(url: string): void {
		this.rest.setBackendUrl(url);
		this.socket.setBackendUrl(url);
	}

	private handleSetToken(payload: { token: string }): void {
		const wasTokenSet = this.isTokenSet;

		this.rest.setToken(payload.token);
		this.socket.setToken(payload.token);

		this.setTokenRefreshInterval(payload.token);

		this.tokenRefreshResolve?.();
		this.tokenRefreshResolve = null;
		this.tokenRefreshPromise = null;

		this.isTokenSet = true;

		if (wasTokenSet) {
			const { serverId, rockstarId, accountId, characterId } =
				jwtDecode<IAuthPayload>(payload.token);
			EventBus.trigger('backendTokenUpdated', {
				serverId,
				rockstarId,
				accountId,
				characterId,
			});
		}
	}

	private async handleConnect(): Promise<void> {
		// Для rest не нужно соединений
		await this.socket.connect();

		this.initResolve?.();
		this.initResolve = null;
		this.initPromise = null;

		this.isConnected = true;

		EventBus.trigger('onBackendConnect');

		if (!this.isFirstConnection) {
			EventBus.trigger('onBackendReconnect');
		}

		this.isFirstConnection = false;
	}

	private handleDisconnect(): void {
		this.socket.disconnect();

		this.isConnected = false;
	}

	private setTokenRefreshInterval(token: string): void {
		if (this.tokenRefreshInterval) {
			return;
		}

		const decodedToken = jwtDecode(token);
		const expiresAt = decodedToken.exp * 1000;
		if (!expiresAt) {
			console.warn('Failed to extract expiration time from JWT token');
			return;
		}

		const now = Date.now();
		const timestampDiff = expiresAt - now;
		if (timestampDiff < 0) {
			return;
		}

		const tokenRefreshTimeMs = timestampDiff * 0.9;
		this.tokenRefreshInterval = setInterval(
			this.refreshToken.bind(this),
			tokenRefreshTimeMs,
		);
	}

	@notifyOnError
	private async refreshToken(): Promise<void> {
		if (this.tokenRefreshPromise !== null) {
			return;
		}

		this.tokenRefreshPromise = new Promise((resolve) => {
			this.tokenRefreshResolve = resolve;
		});

		await this.rpc.invokeServer<void>('auth:refreshToken');
		await this.waitForTokenRefresh();
	}

	private async waitForTokenRefresh(): Promise<void> {
		await this.tokenRefreshPromise;
	}

	public async addEventHandler<T = unknown>(
		eventName: string,
		handler: (...args: any[]) => T,
	) {
		this.socket.addEventHandler(eventName, handler);
	}

	public async invoke<T, P = unknown>(
		methodName: string,
		payload?: P,
		shouldRetry = true,
	): Promise<T> {
		if (!this.isConnected) {
			throw new Error(`Backend is not connected`);
		}

		await this.waitForTokenRefresh();
		return this.rest.invoke(methodName, payload, shouldRetry);
	}

	private async handleInvoke(payload: {
		callId: number;
		methodName: string;
		payload: unknown;
	}) {
		const { callId, methodName, payload: methodPayload } = payload;
		try {
			const result = await this.rest.invoke(methodName, methodPayload);
			await this.rpc.invokeClient('backend:invokeResult', {
				callId,
				result,
			});
		} catch (e: any) {
			if (e instanceof ClientError) {
				await this.rpc.invokeClient('backend:invokeResult', {
					callId,
					isClientError: true,
					error: e.getCode(),
					params: e.getParams(),
				});
			} else {
				console.error(
					'[BackendService] Error while invoking rpc from client:',
					e,
				);
				await this.rpc.invokeClient('backend:invokeResult', {
					callId,
					error: e?.message || 'Unknown error',
				});
			}
		}
	}

	/** Используется для подключения к реальному беку. См. VITE_DEV_BACKEND */
	public async localConnect(): Promise<void> {
		const useRealBackend = import.meta.env.VITE_DEV_BACKEND === 'true';
		if (!useRealBackend) {
			return;
		}

		const backendUrl = import.meta.env.VITE_DEV_BACKEND_URL;

		const authPayload = {
			serverId: import.meta.env.VITE_DEV_BACKEND_SERVER_ID,
			rockstarId: 'gui-real-backend',
			email: import.meta.env.VITE_DEV_BACKEND_ACCOUNT_EMAIL,
			characterIndex: Number(import.meta.env.VITE_DEV_BACKEND_CHARACTER_INDEX),
			secret: import.meta.env.VITE_DEV_BACKEND_SECRET,
		};

		await this.rpc.callLocal('backend:setUrl', backendUrl);

		const { token: authToken } = await this.rest.invoke<{ token: string }>(
			'accounts:devlogin',
			authPayload,
			true,
		);

		await this.rpc.callLocal('backend:setToken', { token: authToken });
		await this.rpc.callLocal('backend:connect');
	}
}
